import React, { useMemo, useState } from 'react';
import { View, Platform, ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import TextLato from '../text/text-lato/TextLato';
import AnimatedDigit from './AnimatedDigit';
import { styles } from './NumberBadge.styles';
import { brand, neutrals } from 'src/theme/colors';
import { useHeartBeatAnimation } from 'src/hooks/useHeartBeatAnimation';
import { DIGIT_HEIGHT } from './NumberBadge.constants';
interface NumberBadgeProps {
  value: number;
  animated?: boolean;
  animationDuration?: number;
  showWarning?: boolean;
  warningText?: string;
  testID?: string;
  color?: string;
  backgroundColor?: string;
  containerStyle?: ViewStyle;
}

const NumberBadge: React.FC<NumberBadgeProps> = ({
  value,
  animated = true,
  animationDuration = 500,
  showWarning = false,
  warningText,
  testID = '',
  color = neutrals.coldGrays.ice,
  backgroundColor = brand.main.water,
  containerStyle,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const digits = useMemo(() => {
    const sanitizedValue = Math.floor(Math.abs(value));
    return sanitizedValue
      .toString()
      .split('')
      .map(d => parseInt(d, 10));
  }, [value]);

  const { heartbeatAnimatedStyle, badgeAnimatedStyle } = useHeartBeatAnimation({
    showWarning,
    backgroundColor,
  });

  const hoverHandlers =
    Platform.OS === 'web'
      ? {
          onPointerEnter: () => setIsHovered(true),
          onPointerLeave: () => setIsHovered(false),
        }
      : {};

  const shouldShowTooltip = showWarning && warningText && isHovered;

  const badgeContent = animated ? (
    <View style={[styles.digitsContainer, containerStyle]}>
      {digits.map((digit, index) => (
        <AnimatedDigit
          key={`digit-${digits.length - index - 1}`}
          digit={digit}
          height={DIGIT_HEIGHT}
          textStyle={{ ...styles.text, color: color }}
          animationDuration={animationDuration}
        />
      ))}
      <TextLato style={{ display: 'none' }} testID={testID}>
        {value}
      </TextLato>
    </View>
  ) : (
    <TextLato style={[styles.text, { color: color }, containerStyle]} testID={testID}>
      {value}
    </TextLato>
  );

  return (
    <View style={[styles.container]} {...hoverHandlers}>
      {shouldShowTooltip && (
        <View style={styles.warningTooltip}>
          <TextLato style={styles.warningTooltipText}>{warningText}</TextLato>
        </View>
      )}
      {showWarning && <Animated.View style={[styles.heartbeatBorder, heartbeatAnimatedStyle]} />}
      <Animated.View style={[styles.badge, badgeAnimatedStyle]}>{badgeContent}</Animated.View>
    </View>
  );
};

export default NumberBadge;
